import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { AnalyticsNote } from "@/components/Compliance";
import usePageMeta from "@/hooks/usePageMeta";
import { ARTICLES } from "@/data/insights";

export default function Insights() {
  usePageMeta({
    title: "Insights — Financial Market Education & Market Intelligence | Adviserz",
    description:
      "Educational explainers on open interest, options Greeks, volatility, PCR, machine learning and quantitative market research. Informational and educational only — not investment advice.",
    canonical: "https://adviserz.in/insights",
  });

  return (
    <div className="min-h-screen bg-surface-base text-white">
      <SiteHeader active="insights" />

      <section className="relative overflow-hidden border-b border-white/[0.06]">
        <div className="absolute inset-0 grid-bg-fine opacity-60 grid-radial-mask" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 pt-36 md:pt-44 pb-16">
          <div className="overline mb-6">Insights · Learn</div>
          <h1 className="font-display font-medium text-[36px] sm:text-5xl lg:text-[58px] tracking-tighter leading-[1.05] max-w-3xl">
            Financial market education, <span className="brand-text-gradient">explained quantitatively.</span>
          </h1>
          <p className="mt-7 text-zinc-400 max-w-2xl leading-relaxed">
            Concept explainers on derivatives data, volatility, risk and quantitative method — written to build
            understanding, not to prescribe action.
          </p>
          <div className="mt-9 max-w-2xl"><AnalyticsNote /></div>
        </div>
      </section>

      <section className="bg-surface-base">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="label-xs mb-8">Articles · {ARTICLES.length}</div>
          <div className="grid gap-px bg-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden">
            {ARTICLES.map((a, i) => (
              <Link
                key={a.slug}
                to={`/insights/${a.slug}`}
                data-testid={`insight-card-${a.slug}`}
                className="group bg-[#0B0C10] hover:bg-[#101219] transition-colors px-6 md:px-10 py-8 grid md:grid-cols-12 gap-4 items-start"
              >
                <div className="md:col-span-1 font-mono text-xs text-ai-accent pt-1">{String(i + 1).padStart(2, "0")}</div>
                <div className="md:col-span-7">
                  <h2 className="font-display font-medium text-xl md:text-2xl tracking-tight group-hover:text-white">{a.title}</h2>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{a.summary}</p>
                </div>
                <div className="md:col-span-4 flex md:justify-end items-start gap-5">
                  <div className="md:text-right">
                    <div className="label-xs text-zinc-500">{a.category}</div>
                    <div className="mt-2 font-mono text-[11px] text-zinc-600">{a.readTime}</div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-zinc-600 group-hover:text-ai-accent transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
